/**
 * Camino Design System (CDS)
 *
 * A comprehensive design system with semantic APIs for consistent usage.
 * All tokens and components are fully typed for better developer experience.
 */

import { Color } from "./foundation/Color";
import { Spacing } from "./foundation/Spacing";
import { Typography } from "./foundation/Typography";
import { Shadow } from "./foundation/Shadow";
import { Radius } from "./foundation/Radius";
import { Size } from "./foundation/Size";

import { Text } from "./primitives/Text";
import { Text10, Text11, Text12, Text14, Text16, Text18 } from "./primitives/TextSizes";
import { Heading12, Heading14, Heading16, Heading18, Heading20, Heading22 } from "./primitives/HeadingSizes";
import { Span } from "./primitives/Span";
import { Box } from "./primitives/Box";
import { Icon } from "./primitives/Icon";
import { Button } from "./primitives/Button";
import { Input } from "./primitives/Input";
import { Textarea } from "./primitives/Textarea";
import { Link } from "./primitives/Link";
import { Image } from "./primitives/Image";
import { Checkbox } from "./primitives/Checkbox";
import { Radio } from "./primitives/Radio";
import { Toggle } from "./primitives/Toggle";
import { Select } from "./primitives/Select";

import { Callout } from "./components/Callout";
import { Card } from "./components/Card";
import { Modal } from "./components/Modal";
import { Table } from "./components/Table";
import { Navigation } from "./components/Navigation";
import { Form } from "./components/Form";
import { Tabs } from "./components/Tabs";
import { SegmentedControl } from "./components/SegmentedControl";
import { Dropdown } from "./components/Dropdown";
import { AddButton } from "./components/AddButton";
import { UserTag } from "./components/UserTag";
import { Tag } from "./primitives/Tag";
import { NameTag } from "./primitives/NameTag";
import { Toast } from "./components/Toast";
import { Skeleton } from "./components/Skeleton";
import { Divider } from "./components/Divider";
import { SectionSelector } from "./components/SectionSelector";
import { NamePill } from "./components/NamePill";
import { RecipientChip } from "./components/RecipientChip";
import { IconButton } from "./components/IconButton";
import { ActionTypeTag, ActionTypeKey } from "./components/ActionTypeTag";
import { AdminMeetingTypeTag } from "./components/AdminMeetingTypeTag";
import { GeneralMeetingTypeTag } from "./components/GeneralMeetingTypeTag";
import { TaskStatusTag } from "./components/TaskStatusTag";
import { CheckpointStatusTag } from "./components/CheckpointStatusTag";
import { JourneyTag } from "./components/JourneyTag";
import { ActionIconTag } from "./components/ActionIconTag";
import { NewHireJourneyTag } from "./components/NewHireJourneyTag";
import { BasePill } from "./components/BasePill";
import { AttributeTag, AvatarAttributeTag } from "./components/AttributeTag";
import { FieldTag } from "./components/FieldTag";
import { FloatingField } from "./components/FloatingField";

import { Stack } from "./layout/Stack";
import { Grid } from "./layout/Grid";
import { Container } from "./layout/Container";
import { Section } from "./layout/Section";

import { Page } from "./patterns/Page";
import { Workflow } from "./patterns/Workflow";

// Main CDS namespace
const CDS = {
  // Foundation
  Color,
  Spacing,
  Typography,
  Shadow,
  Radius,
  Size,

  // Primitives
  Text,
  Text10,
  Text11,
  Text12,
  Text14,
  Text16,
  Text18,
  Heading12,
  Heading14,
  Heading16,
  Heading18,
  Heading20,
  Heading22,
  Span,
  Box,
  Icon,
  Button,
  Input,
  Textarea,
  Link,
  Image,
  Checkbox,
  Radio,
  Toggle,
  Select,

  // Components
  Callout,
  Card,
  Modal,
  Table,
  Navigation,
  Form,
  Tabs,
  SegmentedControl,
  Dropdown,
  AddButton,
  UserTag,
  Tag,
  NameTag,
  Toast,
  Skeleton,
  Divider,
  SectionSelector,
  NamePill,
  RecipientChip,
  IconButton,
  ActionTypeTag,
  AdminMeetingTypeTag,
  GeneralMeetingTypeTag,
  TaskStatusTag,
  CheckpointStatusTag,
  JourneyTag,
  ActionIconTag,
  NewHireJourneyTag,
  AttributeTag,
  AvatarAttributeTag,
  FieldTag,
  BasePill,

  // Layout
  Stack,
  Grid,
  Container,
  Section,

  // Patterns
  Page,
  Workflow,
} as const;

export default CDS;
export { CDS };

// Export resolver hooks for external components
export {
  useResolveColor,
  useResolveSpacing,
  useResolveSize,
  useResolveShadow,
  useResolveRadius,
} from "./hooks/useResolveToken";

export { useInvertedColor, getInvertedColorToken } from "./hooks/useInvertedColor";

// Re-export types
export type { TagSize } from "./foundation/TagSizeStyles";
export type { FieldTagProps } from "./components/FieldTag";
export type { AttributeTagProps, AvatarAttributeTagProps } from "./components/AttributeTag";

// Re-export individual modules for direct imports if needed
export {
  Color,
  Spacing,
  Typography,
  Shadow,
  Radius,
  Size,
  Text,
  Text10,
  Text11,
  Text12,
  Text14,
  Text16,
  Text18,
  Heading12,
  Heading14,
  Heading16,
  Heading18,
  Heading20,
  Heading22,
  Span,
  Box,
  Icon,
  Button,
  Input,
  Textarea,
  Link,
  Image,
  Checkbox,
  Radio,
  Toggle,
  Select,
  Callout,
  Card,
  Modal,
  Table,
  Navigation,
  Form,
  Tabs,
  SegmentedControl,
  Dropdown,
  AddButton,
  UserTag,
  Tag,
  NameTag,
  Toast,
  Skeleton,
  Divider,
  SectionSelector,
  NamePill,
  RecipientChip,
  IconButton,
  ActionTypeTag,
  AdminMeetingTypeTag,
  GeneralMeetingTypeTag,
  TaskStatusTag,
  CheckpointStatusTag,
  JourneyTag,
  ActionIconTag,
  NewHireJourneyTag,
  AttributeTag,
  AvatarAttributeTag,
  FieldTag,
  BasePill,
  FloatingField,

  // Layout
  Stack,
  Grid,
  Container,
  Section,
  Page,
  Workflow,
};

export type { ActionTypeKey };
